import { assertExists } from '@blocksuite/global/utils';
import { PathFinder } from '../utils/index.js';
const observeOptions = {
    childList: true,
    subtree: true,
};
export class ViewStore {
    constructor(std) {
        this.std = std;
        this._cachedTree = null;
        this._cachedPath = new Map();
        this.viewSpec = new Set();
        this.getChildren = (path) => {
            const node = this.fromPath(path);
            if (!node) {
                return [];
            }
            return node.children;
        };
        this.getNodeView = (node) => {
            for (const [_, spec] of this.viewSpec.entries()) {
                const view = spec.fromDOM(node);
                if (view) {
                    return {
                        ...view,
                    };
                }
            }
            return null;
        };
        this.calculatePath = (node) => {
            const path = this._calculateNodeViewPath(node);
            return path.map(x => x.id);
        };
        this._getViewSpec = (type) => {
            return Array.from(this.viewSpec).find(spec => spec.type === type);
        };
        this._calculateNodeViewPath = (node) => {
            if (this._cachedPath.has(node)) {
                return this._cachedPath.get(node);
            }
            const host = this.std.host;
            const iterate = (node, path) => {
                if (!node || node === host)
                    return path;
                const nodeView = this.getNodeView(node);
                if (!nodeView) {
                    return path;
                }
                const spec = this._getViewSpec(nodeView.type);
                assertExists(spec);
                const next = spec.toDOM(nodeView).parentElement;
                if (!next) {
                    return path;
                }
                return iterate(next, path.concat(nodeView));
            };
            const path = iterate(node, []).reverse();
            this._cachedPath.set(node, path);
            return path;
        };
        this.getNodeViewTree = () => {
            if (this._cachedTree) {
                return this._cachedTree;
            }
            const iterate = (node) => {
                const nodeView = this.getNodeView(node);
                if (!nodeView) {
                    throw new Error('nodeView not found');
                }
                const spec = this._getViewSpec(nodeView.type);
                assertExists(spec);
                const children = spec
                    .getChildren(spec.toDOM(nodeView))
                    .map((child) => iterate(child));
                return {
                    ...nodeView,
                    children,
                };
            };
            const firstBlock = this.std.host.firstElementChild;
            assertExists(firstBlock);
            const tree = {
                id: '__root__',
                path: [],
                children: [iterate(firstBlock)],
            };
            this._cachedTree = tree;
            return tree;
        };
        this.fromPath = (path) => {
            const tree = this.getNodeViewTree();
            return path.reduce((curr, id) => {
                if (!curr) {
                    return null;
                }
                const child = curr.children.find(x => x.id === id);
                if (!child) {
                    return null;
                }
                return child;
            }, tree);
        };
        this.walkThrough = (fn, path = []) => {
            const tree = this.fromPath(path);
            assertExists(tree, `Invalid path to get node in view: ${path}`);
            const iterate = (parent) => (node, index) => {
                const result = fn(node, index, parent);
                if (result === true) {
                    return;
                }
                node.children.forEach(iterate(node));
            };
            tree.children.forEach(iterate(tree));
        };
        this.getParent = (path) => {
            if (path.length === 0) {
                return null;
            }
            return this.fromPath(PathFinder.parent(path));
        };
        this.findPrev = (path, fn) => {
            const getPrev = (path) => {
                const parent = this.getParent(path);
                if (!parent) {
                    return null;
                }
                const index = this._indexOf(path, parent);
                if (index === -1) {
                    return null;
                }
                if (index === 0) {
                    const grandParent = this.getParent(PathFinder.parent(path));
                    if (!grandParent)
                        return null;
                    return {
                        nodeView: parent,
                        parent: grandParent,
                        index: this._indexOf(PathFinder.parent(path), grandParent),
                    };
                }
                return {
                    nodeView: parent.children[index - 1],
                    parent,
                    index: index - 1,
                };
            };
            let output = null;
            const iterate = (path) => {
                const state = getPrev(path);
                if (!state) {
                    return;
                }
                const { nodeView, parent, index } = state;
                const result = fn(nodeView, index, parent);
                if (result) {
                    output = nodeView;
                    return;
                }
                iterate(nodeView.path);
            };
            iterate(path);
            return output;
        };
        this.findNext = (path, fn) => {
            const getNext = (path) => {
                const parent = this.getParent(path);
                if (!parent) {
                    return null;
                }
                const index = this._indexOf(path, parent);
                if (index === -1) {
                    return null;
                }
                if (index === parent.children.length - 1) {
                    const grandParent = this.getParent(PathFinder.parent(path));
                    if (!grandParent)
                        return null;
                    return {
                        nodeView: parent,
                        parent: grandParent,
                        index: this._indexOf(PathFinder.parent(path), grandParent),
                    };
                }
                return {
                    nodeView: parent.children[index + 1],
                    parent,
                    index: index + 1,
                };
            };
            let output = null;
            const iterate = (path) => {
                const state = getNext(path);
                if (!state) {
                    return;
                }
                const { nodeView, parent, index } = state;
                const result = fn(nodeView, index, parent);
                if (result) {
                    output = nodeView;
                    return;
                }
                iterate(nodeView.path);
            };
            iterate(path);
            return output;
        };
        this.indexOf = (path) => {
            const parent = this.getParent(path);
            if (!parent) {
                return -1;
            }
            return this._indexOf(path, parent);
        };
        this._indexOf = (path, parent) => {
            return parent.children.findIndex(x => x.id === path[path.length - 1]);
        };
        this._observer = new MutationObserver(() => {
            this._cachedPath.clear();
            this._cachedTree = null;
        });
    }
    register(spec) {
        this.viewSpec.add(spec);
    }
    viewFromPath(type, path) {
        const tree = this.fromPath(path);
        if (!tree || tree.type !== type) {
            return null;
        }
        return tree.view;
    }
    mount() {
        this._observer.observe(this.std.host, observeOptions);
    }
    unmount() {
        this._cachedPath.clear();
        this._cachedTree = null;
        this._observer.disconnect();
        this.viewSpec.clear();
    }
}
//# sourceMappingURL=view-store.js.map